import {
  buildPhoneValue,
  splitPhoneValue,
  validatePhoneParts,
} from "../../../../../app/hooks/usePhoneValidation";

export const formatDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

export const formatDisplayDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getRangeLength = (startDate, endDate) => {
  if (!startDate || !endDate) return { nbDays: 0, nbNight: 0 };

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return { nbDays: 0, nbNight: 0 };
  }

  const diffMs = end.getTime() - start.getTime();
  const nbDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

  return { nbDays, nbNight: Math.max(0, nbDays - 1) };
};

export const numberValue = (value) => {
  if (typeof value === "object" && value !== null) {
    return Number(value.value ?? 0);
  }

  return Number(value ?? 0);
};

export const isTripDateComplete = (tripDate, tripProgramDetails) => {
  if (!tripDate?.stepsDate || !tripProgramDetails) return false;

  const programIds = tripProgramDetails.map((detail) => detail.id?.trim());
  const allStepIds = tripDate.stepsDate.flatMap((step) =>
    (step.stepIds || []).map((id) => id?.trim())
  );

  const allProgramIdsExist = programIds.every((id) => allStepIds.includes(id));
  const allStepIdsExist = allStepIds.every((id) => programIds.includes(id));

  return allProgramIdsExist && allStepIdsExist;
};

// Téléphone des managers du voyage. Le composant commun InternationalPhoneInput
// travaille sur une valeur combinée « +<indicatif><numéro> » ; l'API attend
// { indicative (sans « + »), number }. Les fonctions de conversion et de
// validation sont celles de usePhoneValidation (partagées avec /profil).

/** Valeur affichée par InternationalPhoneInput pour un manager. */
export const managerPhoneToInputValue = (phoneNumber) =>
  phoneNumber?.number ? buildPhoneValue(phoneNumber) : "";

/** Valeur émise par InternationalPhoneInput → { indicative, number } stocké. */
export const managerPhoneFromInputValue = (value) => {
  if (!value) return { indicative: "", number: "" };
  return splitPhoneValue(value);
};

/**
 * Erreurs du téléphone d'un manager. Rien n'est signalé tant qu'aucun numéro
 * n'est saisi (un manager peut être enregistré sans téléphone, comme avant).
 */
export const getManagerPhoneErrors = (phoneNumber) => {
  if (!phoneNumber?.number) return { indicative: null, number: null };
  return validatePhoneParts(phoneNumber);
};

/** Messages à afficher sous le champ, traduits avec les clés phoneValidation. */
export const managerPhoneErrorList = (phoneNumber, t) => {
  const errors = getManagerPhoneErrors(phoneNumber);
  return [errors.indicative, errors.number]
    .filter(Boolean)
    .map((err) => t(`phoneValidation.${err.code}`, err.message));
};

/** Vrai si au moins un manager a un numéro saisi et invalide. */
export const hasInvalidManagerPhone = (travelManagers = []) =>
  travelManagers.some((manager) => {
    const errors = getManagerPhoneErrors(manager?.phoneNumber);
    return Boolean(errors.indicative || errors.number);
  });
